import React from "react";
import {
  Typography,
  Stack,
  Box,
  List,
  ListItem,
  Card,
  CardActionArea,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import { Link } from "react-router-dom";
import EventChip from "../EventChip";
import dayjs from "dayjs";
import { EventPartialDto } from "../../../types/eventTypes";
import EventRegistrationButton from "../EventRegistrationButton";
import { checkIfEventRegistrationIsOpen } from "../../../utils/EventsUtils";

interface EventsOverviewMobileListProps {
  events: EventPartialDto[];
  checkIfUserIsSignedUpForEvent: (event: EventPartialDto) => boolean;
  onSignUp: (event: EventPartialDto) => Promise<void>;
  onSignOut: (event: EventPartialDto) => Promise<void>;
}

/**
 * Component that displays the events in a list for mobile view, uses the EventsSignUpButton component to display the sign up button for each event
 * @param events The events to display
 * @param checkIfUserIsSignedUpForEvent The function to check if the user is signed up for an event, passed down to the EventRegistrationButton component to display the correct button for each event
 * @param onSignUp The function to sign up for an event, passed down to the EventRegistrationButton component to display the correct button for each event
 * @param onSignOut The function to sign out from an event, passed down to the EventRegistrationButton component to display the correct button for each event
 * @returns A list with the events, sorted by date, and a sign up button for each event if the user is not signed up for it yet and the registration deadline is not in the past
 */
const EventsOverviewMobileList = ({
  events,
  checkIfUserIsSignedUpForEvent,
  onSignUp,
  onSignOut,
}: EventsOverviewMobileListProps) => {
  return (
    <List disablePadding sx={{ mt: 2, mr: 3 }}>
      {events.map((event, index) => (
        <React.Fragment key={event.eventId}>
          {index === 0 || dayjs(event.eventBeginn).month() !== dayjs(events[index - 1].eventBeginn).month() ? (
            <ListItem sx={{ mb: 1, color: "#f6891f", borderBottom: 1 }}>
              <Typography fontWeight={"bold"} variant="body1" sx={{ ml: -2 }}>
                {dayjs(event.eventBeginn).locale("de").format("MMMM YYYY")}
              </Typography>
            </ListItem>
          ) : null}
          <Card
            sx={{ p: 0.5, flexDirection: "column", alignItems: "flex-start", mb: 1, width: "100%" }}
            style={{ boxShadow: "none" }}
          >
            <CardActionArea
              component={Link}
              to={
                event.type === "Workshop" || event.type === "Pflichtworkshop" || event.type === "Externer Workshop"
                  ? `/workshops/${event.workshopId}/${event.eventId}`
                  : `/veranstaltungen/${event.eventId}`
              }
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: "flex", direction: "row", alignItems: "center" }}>
                  <EventChip type={event.type} size="small" sx={{ ml: -0.5, mr: 1 }} />
                  <Typography variant={"body1"} fontWeight={"bold"}>
                    {event.eventName}
                  </Typography>
                </Box>
                <Stack direction="row">
                  <Typography variant="body2" color="textSecondary" fontWeight={"bold"}>
                    Datum: &nbsp;
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {dayjs(event.eventBeginn).format("DD.MM.YYYY")}
                    {dayjs(event.eventEnd) > dayjs(event.eventBeginn)
                      ? " - " + dayjs(event.eventEnd).format("DD.MM.YYYY")
                      : null}
                  </Typography>
                </Stack>
                <Box>
                  {event.registrationTo && event.registrationFrom ? (
                    // check if registration start is in the future
                    dayjs(event.registrationFrom) > dayjs() ? (
                      // then display the registration start
                      <Stack direction="row" alignItems="center">
                        <Typography variant="body2" color="textSecondary" fontWeight={"bold"}>
                          Anmeldung ab: &nbsp;
                        </Typography>
                        <Typography variant={"subtitle2"} color="text.secondary">
                          {dayjs(event.registrationFrom).format("DD.MM.YYYY [/] HH:mm")}
                        </Typography>
                      </Stack>
                    ) : dayjs(event.registrationTo) > dayjs() ? (
                      // else check if registration deadline is in the future and display it
                      <Stack direction="row" alignItems="center">
                        <Typography variant="body2" color="textSecondary" fontWeight={"bold"}>
                          Anmeldung bis: &nbsp;
                        </Typography>
                        <Typography variant={"subtitle2"} color="text.secondary">
                          {dayjs(event.registrationTo).format("DD.MM.YYYY [/] HH:mm")}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center">
                        <Typography variant="body2" color="textSecondary" fontWeight={"bold"}>
                          Anmeldung: &nbsp;
                        </Typography>
                        <Typography variant={"subtitle2"} color="text.secondary">
                          Abgelaufen
                        </Typography>
                      </Stack>
                    )
                  ) : event.registrationTo ? (
                    // if the registrationStart is null check if the registrationDeadline is not null if so display it
                    dayjs(event.registrationTo) > dayjs() ? (
                      <Stack direction="row" alignItems="center">
                        <Typography variant="body2" color="textSecondary" fontWeight={"bold"}>
                          Anmeldung bis: &nbsp;
                        </Typography>
                        <Typography variant={"subtitle2"} color="text.secondary">
                          {dayjs(event.registrationTo).format("DD.MM.YYYY [/] HH:mm")}
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" alignItems="center">
                        <Typography variant="body2" color="textSecondary" fontWeight={"bold"}>
                          Anmeldung: &nbsp;
                        </Typography>
                        <Typography variant={"subtitle2"} color="text.secondary">
                          Abgelaufen
                        </Typography>
                      </Stack>
                    )
                  ) : event.registrationFrom ? (
                    <Stack direction="row" alignItems="center">
                      <Typography variant="body2" color="textSecondary" fontWeight={"bold"}>
                        Anmeldung ab: &nbsp;
                      </Typography>
                      <Typography variant={"subtitle2"} color="text.secondary">
                        {dayjs(event.registrationFrom).format("DD.MM.YYYY [/] HH:mm")}
                      </Typography>
                    </Stack>
                  ) : (
                    <Box />
                  )}
                </Box>
                <Box>
                  {event.startTime ? (
                    <Stack direction="row">
                      <Typography variant="body2" color="textSecondary" fontWeight={"bold"}>
                        Uhrzeit: &nbsp;
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {event.startTime ? event.startTime : null}
                        {event.startTime && !event.endTime ? null : " - "}
                        {event.endTime ? event.endTime : null}
                      </Typography>
                    </Stack>
                  ) : null}
                </Box>
              </CardContent>
            </CardActionArea>
            <CardActions sx={{ p: 0, pt: 0.5 }}>
              <Box sx={{ p: 0, ml: "auto" }}>
                {dayjs(event.eventEnd) > dayjs() ? (
                  <EventRegistrationButton
                    isUserSignedUp={checkIfUserIsSignedUpForEvent(event)}
                    isRegistrationOpen={checkIfEventRegistrationIsOpen(event)}
                    isRegistrationFull={
                      event.maximumParticipants && event.amountOfParticipants
                        ? event.amountOfParticipants >= event.maximumParticipants
                        : false
                    }
                    onSignUp={async () => await onSignUp(event)}
                    onSignOut={async () => await onSignOut(event)}
                    size={"medium"}
                  />
                ) : null}
              </Box>
            </CardActions>
          </Card>
          <Divider sx={{ mb: 2 }} />
        </React.Fragment>
      ))}
    </List>
  );
};

export default EventsOverviewMobileList;
